<template>
  <div>
    <component
      :collections="collections"
      :displayMode="displayMode"
      :showPrimaryHandler="showPrimaryHandler"
      :hidePrimaryHandler="hidePrimaryHandler"
      :toolbarActionItemsGrouped="toolbarActionItemsGrouped"
      :transitionToCollection="transitionToCollection"
      :enlargeImage="enlargeImage"
      :notices="notices"
      :scale="scale"
      :recipe="recipe"
      :is="component"></component>
    <input type="file" ref="formImageInput" v-on:change="onSelectImage" style="display: none;" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { RecipeZeroState } from '@/components/Recipe'
import { RecipeDetailCompact, RecipeDetailRegular } from '@/components'
import {
  ADD_NOTIFICATION,
  SET_SCALE,
  SET_TEMPORARY_VIEW_VISIBILITY
} from '@/modules/mutation-types'
import groupBy from 'lodash.groupby'
import { scaleToString } from '@/models/scale'
import { getWakeLock } from '@/utils/wakelock'
// const RecipeDetailCompact = () => import('@/components/RecipeDetailCompact.vue')
// const RecipeDetailRegular = () => import('@/components/RecipeDetailRegular.vue')

export default {
  components: {
    RecipeDetailCompact,
    RecipeDetailRegular,
    RecipeZeroState
  },
  props: {
    displayMode: String,
    hidePrimaryHandler: Function,
    showPrimaryHandler: Function,
    recipeKey: String
  },
  data () {
    return {
      notices: {
        missingSchema: {
          title: 'No recipe found',
          summary: 'This may occur when a publisher doesn\'t include structured recipe data or when trying to add a URL that is not a recipe. If you believe this page does include a recipe please report an issue and help improve our recipe importer.',
          actions: [
            { name: 'Report an Issue', fn: () => { this.showFeedbackDialog() } }
          ]
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'getRecipeById',
      'isSizeClassCompact'
    ]),
    component () {
      if (!this.recipe) return RecipeZeroState

      return this.isSizeClassCompact
        ? RecipeDetailCompact
        : RecipeDetailRegular
    },
    collections () {
      if (!this.recipe) return

      let collections = Object.values(this.$store.state.Collection.collections)
      return collections.filter(collection => {
        return this.recipe.collectionKeys.indexOf(collection.key) !== -1
      }).sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
    },
    hasKeepScreenOnPreference () {
      return this.$store.state.User.preferences.keepScreenOn
    },
    recipe () {
      return this.getRecipeById(this.recipeKey)
    },
    recipePreferences () {
      return this.$store.state.Recipe.local[this.recipeKey] || {}
    },
    toolbarActionItems () {
      let actions = []
      if (!this.recipe) return []
      if (this.$preference('smartQuantities')) {
        actions.push({
          id: 'scale-recipe',
          class: { active: this.scale !== '1' },
          fn: this.showScaleDialog,
          showAsAction: 'always',
          name: `${this.scaleStr}<small class='finesp'>&nbsp;</small><span class='symbol'>x</span>`
        })
      }
      // favoriteAction
      actions.push({
        id: 'favorite-recipe',
        fn: this.toggleFavorites,
        icon: this.recipe.favorited ? 'star' : 'star_border',
        showAsAction: 'never',
        name: this.recipe.favorited ? 'Remove from Favorites' : 'Add to Favorites'
      })
      // editAction
      actions.push({
        id: 'edit-recipe',
        fn: this.showEditDialog,
        icon: 'create',
        showAsAction: 'never',
        name: 'Edit recipe'
      })
      // addImageAction
      if (!this.recipe.hasImage) {
        actions.push({
          id: 'add-image',
          fn: (index, ev) => {
            this.onAddImage(ev)
          },
          icon: 'add',
          showAsAction: 'never',
          name: 'Add image'
        })
      }
      // removeImageAction
      if (this.recipe.hasImage) {
        actions.push({
          id: 'remove-image',
          fn: this.onRemoveImage,
          icon: 'delete',
          showAsAction: 'never',
          name: 'Remove image'
        })
      }
      // tagRecipeAction
      if (this.$hasFeatureAccess('forceTag')) {
        actions.push({
          id: 'tag-recipe',
          fn: this.onTagRecipe,
          showAsAction: 'never',
          name: 'Tag recipe'
        })
      }
      // exportAction
      actions.push({
        id: 'export',
        fn: this.copyToClipboard,
        icon: 'content_copy',
        showAsAction: 'never',
        name: 'Copy recipe'
      })
      // feedbackAction
      actions.push({
        id: 'leave-feedback',
        fn: this.showFeedbackDialog,
        icon: 'feedback',
        showAsAction: 'never',
        name: 'Report error'
      })
      // deleteAction
      actions.push({
        id: 'delete-recipe',
        fn: this.onDelete,
        icon: 'delete',
        showAsAction: 'never',
        name: 'Delete recipe'
      })
      // addToAction
      actions.push({
        id: 'add-to-collection',
        fn: this.showAddToCollectionDialog,
        icon: this.recipe.inAnyCollection() ? 'bookmark' : 'bookmark_border',
        showAsAction: 'always',
        name: 'Add to Collection'
      })
      // processingActionState
      actions.push({
        id: 'processing',
        fn: null,
        showAsAction: 'always',
        showIfProcessing: true,
        name: 'Processing'
      })

      return actions.filter(action => {
        return Boolean(action.showIfProcessing) === this.recipe.isProcessing
      })
    },
    scale () {
      return this.recipePreferences.scale || '1'
    },
    scaleStr () {
      return scaleToString(this.scale)
    },
    toolbarActionItemsGrouped () {
      return groupBy(this.toolbarActionItems, (actionItem) => {
        return actionItem.showAsAction
      })
    }
  },
  methods: {
    copyToClipboard () {
      navigator.clipboard.writeText(this.recipe.text)
        .then(() => {
          this.$store.commit(ADD_NOTIFICATION, {
            message: 'Copied to clipboard'
          })
        })
        .catch(() => {
          this.$store.commit(ADD_NOTIFICATION, {
            message: 'Could not copy recipe'
          })
        })
    },
    deleteHandler () {
      let key = this.recipe.key
      this.showPrimaryHandler()
      this.$store.dispatch('deleteRecipe', {
        uid: this.$store.state.User.uid,
        key
      }).then(() => {
        // TODO: Select next item in list on delete or none
        const path = this.$urlHelpers.collectionPath(this.$route.params.collectionKey)
        this.$router.currentRoute.path !== path && this.$router.replace(path)
        this.$track('recipe.delete', {
          context: 'recipe',
          ability: 'delete',
          recipeId: key
        })
      }).catch(err => {
        this.$store.commit(ADD_NOTIFICATION, {
          message: 'Unable to delete recipe. Refresh to try again.'
        })
        console.log(err)
      })
    },
    enlargeImage () {
      this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
        name: 'ImageDialog',
        props: {
          height: this.recipe.image.height,
          placeholder: this.recipe.imageUrl('placeholder'),
          url: this.recipe.imageUrl('full'),
          width: this.recipe.image.width,
          backdrop: 'active'
        }
      })
    },
    onAddImage () {
      const el = this.$refs.formImageInput
      if (el && document.createEvent) {
        var mouseEvent = document.createEvent('MouseEvents')
        mouseEvent.initEvent('click', true, false)
        el.dispatchEvent(mouseEvent)
      }
    },
    onDelete () {
      this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
        name: 'ConfirmDialog',
        props: {
          title: 'Delete recipe?',
          message: `This recipe will be deleted from every device that syncs your library and can't be undone.`,
          acceptItem: {
            name: 'Delete',
            fn: () => {
              this.deleteHandler()
              return true
            }
          },
          dismissItem: {
            name: 'Cancel'
          }
        }
      })
      this.$track(`recipe.action.select`, {
        recipeId: this.recipe.key,
        name: 'delete'
      })
    },
    onSelectImage (ev) {
      let file = ev.target.files[0]
      this.$refs.formImageInput.value = ''
      this.$store.dispatch('uploadImage', {
        file,
        recipeKey: this.recipe.key,
        userId: this.$store.state.User.uid
      }).catch(() => {
        this.$store.commit(ADD_NOTIFICATION, {
          message: 'Unable to save image'
        })
      })
      this.$track(`recipe.action.select`, {
        recipeId: this.recipe.key,
        name: 'select-image'
      })
    },
    onTagRecipe () {
      this.$store.dispatch('tagRecipe', {
        userId: this.$store.state.User.uid,
        recipeKey: this.recipe.key
      })
      this.$track(`recipe.action.select`, {
        recipeId: this.recipe.key,
        name: 'tag'
      })
    },
    onRemoveImage () {
      this.$store.dispatch('removeRecipeImage', {
        userId: this.$store.state.User.uid,
        recipeKey: this.recipe.key
      })
      this.$track(`recipe.action.select`, {
        recipeId: this.recipe.key,
        name: 'remove-image'
      })
    },
    showEditDialog () {
      this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
        name: 'RecipeEditDialog',
        props: {
          recipeKey: this.recipe.key
        }
      })
      this.$track(`recipe.action.select`, {
        recipeId: this.recipe.key,
        name: 'edit'
      })
    },
    showAddToCollectionDialog () {
      this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
        name: 'AddToCollectionDialog',
        props: {
          recipeKey: this.recipe.key
        }
      })
      this.$track(`recipe.action.select`, {
        recipeId: this.recipe.key,
        name: 'add-to-collection'
      })
    },
    showFeedbackDialog () {
      this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
        name: 'Feedback',
        props: {
          placeholder: 'Describe the issue you\'re seeing...'
        }
      })
      this.$track(`recipe.action.select`, {
        recipeId: this.recipe.key,
        name: 'feedback'
      })
    },
    showScaleDialog () {
      this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
        name: 'ScaleDialog',
        props: {
          scale: this.scale.toString(),
          selectHandler: this.onChangeScale
        }
      })
      this.$track(`recipe.action.select`, {
        recipeId: this.recipe.key,
        name: 'scale'
      })
    },
    onChangeScale (scale) {
      let recipeKey = this.recipe.key
      this.$store.commit(SET_SCALE, {
        recipeKey,
        scale
      })
      this.$track(`recipe.scale`, {
        recipeId: this.recipe.key,
        from: this.scale,
        to: scale
      })
    },
    toggleFavorites () {
      if (!this.recipe.key) return

      let collectionKey = 'favorites'
      let type = this.recipe.favorited
        ? 'removeFromCollection'
        : 'addToCollection'
      let uid = this.$store.state.User.uid
      let key = this.recipe.key
      this.$store.dispatch({type, uid, key, collectionKey})
      this.$track(`recipe.${this.recipe.favorited ? 'favorite' : 'unfavorite'}`, {
        recipeId: this.recipe.key
      })
    },
    toggleWakeLock () {
      if (this.recipeKey && this.hasKeepScreenOnPreference) {
        getWakeLock().enable()
      } else {
        getWakeLock().disable()
      }
    },
    transitionToCollection (collectionId, recipeId) {
      const path = (recipeId)
        ? this.$urlHelpers.collectionRecipePath(collectionId, recipeId)
        : this.$urlHelpers.collectionPath(collectionId)
      if (this.$route.path !== path) this.$router.push(path)

      this.showPrimaryHandler()
    },
    trackView () {
      this.$track('Recipe Viewed', {
        context: 'recipe',
        ability: 'view',
        id: this.recipeKey
      })
    },
    updateTitle () {
      const parts = ['Cookmark']
      if (this.recipe && this.recipe.name) parts.unshift(this.recipe.name)
      document.title = parts.join(' - ')
    }
  },
  watch: {
    recipeKey: {
      immediate: true,
      handler (recipeKey) {
        if (recipeKey) {
          getWakeLock().enable()
          this.trackView()
        } else {
          getWakeLock().disable()
        }
        this.updateTitle()
      }
    },
    hasKeepScreenOnPreference () {
      this.toggleWakeLock()
    }
  }

}
</script>

<style lang="sass">
@import "../../styles/variables.scss"
.cm-button#scale-recipe
  padding-right: 8px
  text-transform: none
  span
    border: 1.5px solid transparent
    border-radius: 3px
    padding: 0
    font-weight: 500
    font-size: 24px
    margin-top: -2px
    display: inline-block
  small.finesp
    font-size: 0.125em
  span.symbol
    font-size: 19px
  &.active
    span
      color: #1A73E8
.mui-theme--dark
  .cm-button.active#scale-recipe
    span
      color: white
      // 1A73E8
      // background: blue
      // border-color: blue
</style>



// WEBPACK FOOTER //
// src/components/Recipe/Detail.vue